// The change request project's travel/expense and product lines as Show
// displays them. Change request create copies neither, so every line here is
// one the change request adds.

export interface ChildExpense {
  id: string;
  description: string;
  category: string;
  quantity: number;
  revenue: number; // the line's price
}

export interface ChildProduct {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  price: number; // the line total (hardware-price)
  billingFrequency: string;
}

export interface ProductTotal {
  billingFrequency: string;
  label: string;
  total: number;
}

// Decimal attributes may arrive as strings.
const num = (value: unknown) => {
  const n = parseFloat(String(value));
  return isNaN(n) ? 0 : n;
};

export const toChildExpenses = (
  response: { data?: any[]; included?: any[] } | undefined
): ChildExpense[] =>
  (response?.data ?? []).map((expense) => {
    const categoryId = expense.relationships?.["expense-category"]?.data?.id;
    const category = response?.included?.find(
      (item) =>
        item.type === "expense-categories" && String(item.id) === String(categoryId)
    );
    return {
      id: String(expense.id),
      description: expense.attributes?.description || "",
      category: category?.attributes?.name ?? "",
      quantity: num(expense.attributes?.quantity),
      revenue: num(expense.attributes?.revenue),
    };
  });

export const expenseRate = (expense: ChildExpense): number | null =>
  expense.quantity > 0 ? expense.revenue / expense.quantity : null;

export const expensesTotal = (expenses: ChildExpense[]) =>
  expenses.reduce((sum, e) => sum + e.revenue, 0);

export const toChildProducts = (data: any[] | undefined): ChildProduct[] =>
  (data ?? []).map((product) => ({
    id: String(product.id),
    name: product.attributes?.name || "",
    quantity: num(product.attributes?.quantity),
    unitPrice: num(product.attributes?.["unit-price"]),
    price: num(product.attributes?.["hardware-price"]),
    billingFrequency: product.attributes?.["billing-frequency"] || "one_time",
  }));

// The Products editor's billing options, in display order.
const BILLING_LABELS: Record<string, string> = {
  one_time: "One time",
  monthly: "Monthly",
  "bi-monthly": "Bi-monthly",
  quarterly: "Quarterly",
  "semi-annual": "Semi-annual",
  yearly: "Yearly",
};

export const billingLabel = (frequency: string) =>
  BILLING_LABELS[frequency] ?? frequency;

// One-time and recurring amounts aren't comparable, so each frequency gets
// its own total. Known frequencies first, in order; unknown ones after.
export const productTotals = (products: ChildProduct[]): ProductTotal[] => {
  const totals = new Map<string, number>();
  products.forEach((p) =>
    totals.set(p.billingFrequency, (totals.get(p.billingFrequency) ?? 0) + p.price)
  );
  const known = Object.keys(BILLING_LABELS).filter((f) => totals.has(f));
  const unknown = Array.from(totals.keys()).filter((f) => !(f in BILLING_LABELS));
  return [...known, ...unknown].map((billingFrequency) => ({
    billingFrequency,
    label: billingLabel(billingFrequency),
    total: totals.get(billingFrequency) as number,
  }));
};
